import { createReducer } from "@reduxjs/toolkit";
import { DelegatedPedestrianRight } from "../../models/delegations/DelegatedPedestrianRight";
import { ErrorDetailsModel } from "../../models/ErrorDetailsModel";
import { GetDelegatedPedestrianRightsQuery } from "../../api/pedestrianRights.api";
import { 
    clearDelegeeDetailsAction, 
    disposeErrorAction, 
    getPedestrianProductsFailureAction, 
    getPedestrianProductsSuccessAction, 
    inviteDelegeeFailureAction, 
    inviteDelegeeRequestAction, 
    inviteDelegeeSuccessAction, 
    myProductsPedestrianRightsFailureAction, 
    myProductsPedestrianRightsRequestAction, 
    myProductsPedestrianRightsSuccessAction, 
    resendInviteAction, 
    revokePedestrianRightsSuccessAction, 
    toggleShowRevokePedestrianAction,
    deletePedestrianDelegeesSuccessAction,
    toggleShowDeletePedestrianDelegeesAction
} from "../actions/pedestrianRights.actions";
import { DelegeeWithProductsModel } from "../../models/delegations/DelegatedParkingProductsModel";
import { DataType } from "./parking-products";
import { PedestrianProductModel } from "../../models/PedestrianProductModel";
import { reduceRevokePedestrianMyProducts, reduceDeletePedestrianMyProducts, reduceInvitePedestrianMyProducts } from "../helpers/pedestrian-optimistic-updates";

export type PedestrianRightsPagedData<T> = {
    totalPages: number;
    totalRecords: number;
    pages: {
        [page: number]: {
            loading: boolean;
            data?: T[] | null;
            error?: ErrorDetailsModel | null;
            query: GetDelegatedPedestrianRightsQuery;
        };
    };
}

export type PedestrianRightsState = {
    loading: boolean;
    myProducts : PedestrianRightsPagedData<DelegatedPedestrianRight>;
    pedestrianProducts: PedestrianProductModel[];
    error?: ErrorDetailsModel;
    delegeeDetails: DataType<DelegeeWithProductsModel>;
    showRevokePedestriansWarning: boolean;
    showDeletePedestrianDelegeesWarning: boolean;
}

const initialState: PedestrianRightsState = {
    loading: false,
    error: undefined,
    myProducts: {
        pages: {},
        totalPages: 0,
        totalRecords: null!,
    },
    pedestrianProducts: [],
    delegeeDetails: {
        loading: false,
        data: null,
        error: null,
    },
    showRevokePedestriansWarning: false,
    showDeletePedestrianDelegeesWarning: false,
}

export const pedestrianRightsReducer = createReducer(initialState, (builder) => {
    builder
        .addCase(myProductsPedestrianRightsRequestAction, (state, action) => {
            state.myProducts.pages[action.payload.query.page] = {
                loading: true,
                query: action.payload.query,
            };
        })
        .addCase(myProductsPedestrianRightsSuccessAction, (state, action) => {
            state.myProducts.totalPages = action.payload.totalPages;
            state.myProducts.totalRecords = action.payload.totalRecords;
            state.myProducts.pages[action.payload.query.page] = {
                loading: false,
                data: action.payload.data,
                query: action.payload.query,
            };
        })
        .addCase(myProductsPedestrianRightsFailureAction, (state, action) => {
            state.myProducts.pages[action.payload.query.page] = {
                loading: false,
                query: action.payload.query
            };
        })
        .addCase(getPedestrianProductsSuccessAction, (state, action) => {
            state.pedestrianProducts = action.payload.data
        })
        .addCase(getPedestrianProductsFailureAction, (state, action) => {
            state.error = action.payload.response?.data as ErrorDetailsModel
        })
        .addCase(inviteDelegeeRequestAction, (state, _) => {
            state.loading = true;
        })
        .addCase(inviteDelegeeSuccessAction, (state, action) => {
            state.myProducts = reduceInvitePedestrianMyProducts(state.myProducts, state.pedestrianProducts, action.payload!)
            state.loading = false;
        })
        .addCase(inviteDelegeeFailureAction, (state, action) => {
            state.error = action.payload.response?.data as ErrorDetailsModel;
            state.loading = false;
        })
        .addCase(clearDelegeeDetailsAction, (state, _) => {
            state.delegeeDetails = initialState.delegeeDetails
        })
        .addCase(disposeErrorAction, (state, _) => {
            state.loading = initialState.loading;
            state.error = initialState.error;
        })
        .addCase(toggleShowRevokePedestrianAction, (state) => {
            state.showRevokePedestriansWarning = !state.showRevokePedestriansWarning;
        })
        .addCase(revokePedestrianRightsSuccessAction, (state, action) => {
            state.myProducts = reduceRevokePedestrianMyProducts(state.myProducts, action.payload!)
        })
        .addCase(resendInviteAction.pending, (state) => { 
            state.loading = true;
        })
        .addCase(resendInviteAction.fulfilled, (state) => { 
            state.loading = false;
        })
        .addCase(resendInviteAction.rejected, (state) => { 
            state.loading = false;
        })
        .addCase(toggleShowDeletePedestrianDelegeesAction, (state) => {
            state.showDeletePedestrianDelegeesWarning = !state.showDeletePedestrianDelegeesWarning;
        })
        .addCase(deletePedestrianDelegeesSuccessAction, (state, action) => {
            state.myProducts = reduceDeletePedestrianMyProducts(state.myProducts, action.payload!)
        });
});